import React, { useRef } from 'react'
import { useEffect, useState, useCallback } from 'react';
import matterService from '@/services/matter/matterService';
import { useAbility } from '@/providers/';
import { toast } from 'sonner';
import { KeenIcon } from '@/components';
import { Can } from '@casl/react';
import { Link, useNavigate } from 'react-router-dom';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useToolbar } from '@/providers/ToolbarProvider';
import { useConfirmationModal } from '@/hooks';
import DataTable from 'datatables.net-react';
import DT from 'datatables.net-dt';
import 'datatables.net-select-dt';
import 'datatables.net-responsive-dt';
import { createRoot } from 'react-dom/client';
import { SelectInput, DateInput, MATTER_STATUS_OPTIONS, FilterContainer } from '@/components/TableFilter'
import { cn } from '@/lib/utils';
import { renderMultiAvatars, FiltersToFormData, getAccessToken, decodeHtmlEntities } from '@/utils'
import { createDataTableOptions, createDataTableConfig } from '@/utils';
import { useAuthContext } from '@/auth';
import { goTo403 } from "@/utils/Navigation";
import * as Yup from 'yup';
import { RequestFileUploadModal } from '@/components/modal';
import { useUserOptions, useTagOptions } from '@/hooks';
import bulkDeleteService from '@/services/bulk-delete/bulkDeleteService';
import useDataTableBulkDelete from '@/hooks/useDataTableBulkDelete';
import { BulkDeleteActions } from '@/components/BulkDeleteActions';
import { ConfirmationModal } from '@/components/modal';

DataTable.use(DT);
const MattersTab = ({ contactID, contactData }) => {
  const { logout } = useAuthContext();
  const { ability } = useAbility();
  const Navigate = useNavigate()
  const { setConfig } = useToolbar();
  const { isOpen, actionId, openModal, closeModal } = useConfirmationModal();
  const [showFilter, setShowFilter] = useState(false)
  const [filterData, setFilterData] = useState({
    assigned_to: null,
    status: null,
    created_at: null,
    tags: null,
    from_date: null,
    to_date: null,
    date_range: null,
    created_by: null,
    contact_id: contactID,
  });
  const tableRef = useRef(null);
  const token = getAccessToken();
  const filterRef = useRef(filterData);
  const [docUploadModal, setDocUploadModal] = useState(false)
  const [docUploadRowId, setDocUploadRowId] = useState(null)
  const [docUploadLoading, setDocUploadLoading] = useState(false)
  const canCreateMatter = ability.can('handle', 'matter');
  const canDeleteMatter = ability.can('delete', 'matter');
  const loadUserOptions = useUserOptions({ defaultPageSize: 20 });
  const loadTagOptions = useTagOptions({ defaultPageSize: 20 });
  const {
    selectedRows,
    allSelected,
    bulkDeleteLoading,
    bulkDeleteOpen,
    openBulkDelete,
    closeBulkDelete,
    confirmBulkDelete,
    toggleSelectPage,
  } = useDataTableBulkDelete({
    tableRef,
    model: 'Matter',
    deleteFn: bulkDeleteService.bulkDelete,
    onSuccess: () => {
      refresh();
      toast.success('Matters Deleted', {
        description: 'Selected matters deleted successfully.',
      });
    },
    onError: (error) => {
      console.error('Error', error)
      if (error?.message?.includes(`Can't delete`)) {
        toast.warning(`Can't Delete Matter.`, {
          description: error.message || 'An error occurred while trying to delete matters. Please try again later.',
        });
        return;
      }
      toast.error('Failed to delete Matters.', {
        description: 'An error occurred while trying to delete matters. Please try again later.',
      });
    },
  });


  const validationSchema = Yup.object().shape({
    message: Yup.string()
      .max(500, 'Message cannot exceed 500 characters'),
    contact_id: Yup.string().required('Please select a Patient'),
  });

  useEffect(() => {
    filterRef.current = filterData;
  }, [filterData]);

  const badgeClass = {
    open: 'badge-success',
    pending: 'badge-info',
    archived: 'badge-warning',
    closed: 'badge-danger',
  }

  const headerCheckboxId = 'matter-table-select-all';

  const syncHeaderCheckbox = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    const headerCheckbox = document.getElementById(headerCheckboxId);
    if (!headerCheckbox) return;
    const totalOnPage = table.rows({ page: 'current' }).count();
    const selectedOnPage = table.rows({ selected: true, page: 'current' }).count();
    headerCheckbox.checked = totalOnPage > 0 && totalOnPage === selectedOnPage;
    headerCheckbox.indeterminate = selectedOnPage > 0 && selectedOnPage < totalOnPage;
  }, [tableRef]);

  const setupHeaderCheckbox = useCallback((api) => {
    const headerCell = api.column('checkbox:name').header();
    if (!headerCell) return;
    headerCell.innerHTML = `
      <label class="inline-flex items-center cursor-pointer">
        <input id="${headerCheckboxId}" type="checkbox" class="checkbox checkbox-sm cursor-pointer form-checkbox" />
      </label>
    `;
    const headerCheckbox = headerCell.querySelector(`#${headerCheckboxId}`);
    if (headerCheckbox) {
      headerCheckbox.addEventListener('change', (event) => {
        event.stopPropagation();
        const table = tableRef?.current?.dt?.();
        if (!table) return;
        if (event.target.checked) {
          table.rows({ page: 'current' }).select();
        } else {
          table.rows({ page: 'current' }).deselect();
        }
        syncHeaderCheckbox();
      });
    }
  }, [syncHeaderCheckbox]);

  // Reflect each row's selection state on its (display-only) checkbox input.
  const syncRowCheckboxes = useCallback(() => {
    const table = tableRef?.current?.dt?.();
    if (!table) return;
    table.rows({ page: 'current' }).every(function () {
      const node = this.node();
      if (!node) return;
      const input = node.querySelector('.row-select-checkbox');
      if (input) input.checked = node.classList.contains('selected');
    });
  }, [tableRef]);

  const columns = [
    // {
    //   title: 'S#',
    //   name: 'serial',
    //   data: null,
    //orderable: true,se,
    //   render: (data, type, row, meta) => meta.row + 1,
    // },
    {
      data: null,
      defaultContent: '',
      className: 'dt-row-select cursor-pointer text-center',
      id: 'matter-checkbox-select',
      orderable: false,
      name: 'checkbox',
      render: function () {
        // Real styled checkbox so row selection matches the header "select all"
        // checkbox. pointer-events-none lets the click fall through to the cell,
        // which DataTables Select uses to toggle selection (see select.selector).
        return '<input type="checkbox" tabindex="-1" aria-label="Select row" class="checkbox checkbox-sm form-checkbox row-select-checkbox pointer-events-none" />';
      },
    },
    {
      title: 'Name',
      data: null,
      name: 'name',
      render: function (data, type, row) {
        return `<div id="matter-cell-${row.id}"></div>`;
      }
    },
    {
      title: 'Assigned To',
      data: null,
      orderable: true,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="assigned-to-${row.id}"></div>`;
      }
    },
    {
      data: 'tags',
      title: 'Tags',
      className: 'whitespace-normal',
      orderable: true,
      render: function (data, type, row) {
        if (!Array.isArray(data)) return '';
        const tagSpans = data
          .map(tag => `<span class="capitalize whitespace-nowrap badge badge-pill badge-outline badge-primary mr-1 mb-1">${tag.name}</span>`)
          .join('');
        return tagSpans;
      }

    },
    {
      data: 'status',
      title: 'Status',
      orderable: true,
      render: function (value) {
        return `<span class="badge badge-pill badge-outline capitalize ${badgeClass[value]} gap-1 items-center">
            <span class="badge badge-dot size-1.5 ${badgeClass[value]}"></span>
            ${value}
          </span>`
      }
    },
    {
      data: 'originating_attorney',
      title: 'Originating Attorney',
      orderable: true,
    },
    // {
    //   data: null,
    //   title: 'Originating Attorney',
    //   orderable: true,
    //   render: function (data, type, row) {
    //     return `<div id="originating-attorney-${row.id}"></div>`;
    //   }
    // },
    { data: 'created_at', title: 'Created At' },
    {
      title: 'Actions',
      data: null,
      orderable: false,
      searchable: false,
      render: function (data, type, row) {
        return `<div id="action-cell-${row.id}"></div>`;
      }
    }
  ];

  const ajaxConfig = createDataTableConfig('/matters/data_table_list', filterRef, token, logout);

  const options = createDataTableOptions({
    order: [[6, 'desc']],
    select: {
      style: 'multi',
      selector: 'td.dt-row-select'
    },
    initComplete: function () {
      const api = this.api();
      setupHeaderCheckbox(api);
      api.on('select deselect draw', () => {
        syncHeaderCheckbox();
        syncRowCheckboxes();
      });
      api.on('page', () => {
        api.rows().deselect();
      });
      syncHeaderCheckbox();
    },
    drawCallback: function () {
      const api = this.api();
      const rows = api.rows({ page: 'current' }).data();
      syncHeaderCheckbox();
      syncRowCheckboxes();

      rows.each((row) => {
        const container = document.getElementById(`assigned-to-${row.id}`);
        if (container) {
          if (!container._reactRoot) {
            container._reactRoot = createRoot(container);
          }
          container._reactRoot.render(renderMultiAvatars(row.assigned_to));
        }

        const matterContainer = document.getElementById(`matter-cell-${row.id}`);
        if (matterContainer) {
          if (!matterContainer._reactRoot) {
            matterContainer._reactRoot = createRoot(matterContainer);
          }
          matterContainer._reactRoot.render(renderMatterName(row));
        }

        const originatingContainer = document.getElementById(`originating-attorney-${row.id}`);
        if (originatingContainer) {
          if (!originatingContainer._reactRoot) {
            originatingContainer._reactRoot = createRoot(originatingContainer);
          }
          originatingContainer._reactRoot.render(renderMultiAvatars(row.originating_attorney));
        }

        const actionContainer = document.getElementById(`action-cell-${row.id}`);
        if (actionContainer) {
          if (!actionContainer._reactRoot) {
            actionContainer._reactRoot = createRoot(actionContainer);
          }
          actionContainer._reactRoot.render(renderCustomActions(row));
        }
      });
    },
  });

  const resetFilters = () => {
    const cleared = {
      assigned_to: null,
      status: null,
      created_at: null,
      tags: null,
      from_date: null,
      to_date: null,
      date_range: null,
      created_by: null,
      contact_id: contactID,
    };

    setFilterData(cleared);
    filterRef.current = cleared;
    refresh()
  };

  const handleFilterChange = (key, val) => {
    setFilterData((prev) => {
      const updated = { ...prev, [key]: val };
      filterRef.current = updated;
      console.log('updated', updated)
      return updated;
    });
  };

  const handleToggleFilter = () => {
    setShowFilter(prev => !prev)
  }

  const refresh = () => {
    const table = tableRef.current.dt();
    table.ajax.reload();
  };

  const handleDelete = async () => {
    closeModal();
    try {
      const { status } = await matterService.deleteMatter(actionId);
      if (status) {
        refresh();
        toast.success('Matter Deleted.', {
          description: 'Matter has been deleted Successfully.',

        });
      }
    } catch (error) {
      console.error('Error', error)
      if (error.message.includes(`Can't delete`)) {
        toast.warning(`Can't Delete Matter.`, {
          description: error.message || 'An error occurred while trying to delete a matter. Please try again later.',
        });
        return
      }
      toast.error('Failed to delete Matter.', {
        description: 'An error occurred while trying to delete a matter. Please try again later.',

      });
    }
  };

  const handleEdit = (id) => {
    Navigate(`/matters/edit/${id}`)
    // Navigate(`/matters/edit/${id}?tab=About`)
  }

  const handleNavigateMatter = (id) => {
    Navigate(`/matters/detail/${id}`)
    // Navigate(`/matters/detail/${id}?tab=About`)
  }

  const renderMatterName = (row) => {
    const { id, name } = row;
    const fullName = decodeHtmlEntities(name || ""); // ensure safe decode

    return (
      <span
        onClick={() => handleNavigateMatter(id || '')}
        className="cursor-pointer link"
      >
        {fullName}
      </span>
    );
  }

  const openFileUploadModal = (id) => {
    setDocUploadRowId(id);
    setDocUploadModal(true);
  }

  const closeFileUploadModal = () => {
    setDocUploadModal(false);
    setDocUploadRowId(null);
  }

  const handleRequestFileUpload = async (message, contact_id) => {
    if (!contactData?.email?.trim()) {
      toast.warning('Missing Email Address', {
        description: 'This contact has no email. Please add one before proceeding.',

      });
      return
    }
    setDocUploadLoading(true);
    try {
      const { status } = await matterService.requestFileUpload(docUploadRowId, { message, contact_id });
      if (status) {
        closeFileUploadModal();
        toast.success('File Upload Request Sent', {
          description: 'The contact has been requested to upload their document.',

        });
      }
    } catch (error) {
      console.error('Error', error);
      toast.error('Failed to send request for file upload.', {
        description: 'An error occurred while trying to send request for file upload. Please try again later.',

      });
    } finally {
      setDocUploadLoading(false);
    }
  };

  const renderCustomActions = (row) => {
    const { id, contact } = row;
    const canRequestFileUpload = contact.allow_file_upload === 1 && contact.contact_type?.name === 'Provider';
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="text-gray-600">
            <KeenIcon icon="dots-vertical" className="text-[18px]" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          {/* <Can I="edit" a="users" ability={ability}> */}
          <DropdownMenuItem onClick={() => handleEdit(id)}>
            <KeenIcon icon="notepad-edit" className="mr-0.5 h-4 w-4 cursor-pointer" /> Edit
          </DropdownMenuItem>
          {/* row.status !== 'archived' && row.status !== 'closed' && */}
          {canRequestFileUpload && (
            <DropdownMenuItem onClick={() => openFileUploadModal(id)} className="cusror-pointer">
              <KeenIcon icon="file-up" className="mr-0.5 h-4 w-4" /> Request File Upload
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
            <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Delete
          </DropdownMenuItem>
          {/* </Can> */}
          {/* <Can I="delete" a="users" ability={ability}> */}
          {/* <DropdownMenuItem onClick={() => openModal(id)} className="text-red-600 cusror-pointer">
              <KeenIcon icon="trash" className="mr-0.5 h-4 w-4" /> Archive
            </DropdownMenuItem> */}
          {/* </Can> */}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  return (
    <>
      {/* Filters */}
      <FilterContainer
        show={showFilter}
        onApply={() => refresh()}
        onReset={resetFilters}
      >
        <SelectInput
          label="Created By"
          type="users"
          value={filterData.created_by}
          onChange={(val) => handleFilterChange('created_by', val)}
          loadOptions={loadUserOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        />

        <SelectInput
          isMulti={true}
          label="Assigned To"
          type="users"
          value={filterData.assigned_to}
          onChange={(val) => handleFilterChange('assigned_to', val)}
          loadOptions={loadUserOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        />

        <SelectInput
          label="Status"
          value={filterData.status}
          onChange={(val) => handleFilterChange('status', val)}
          staticOptions={MATTER_STATUS_OPTIONS}
          customClass="sm:w-[32%] w-full"
        />

        <SelectInput
          label="Tags"
          type="tags"
          isMulti={true}
          value={filterData.tags}
          onChange={(val) => handleFilterChange('tags', val)}
          loadOptions={loadTagOptions}
          pageSize={20}
          customClass="sm:w-[32%] w-full"
        />

        <DateInput
          customClass="sm:w-[32%] w-full"
          label="Created At - (From, To)"
          mode="range"
          value={filterData.date_range}
          onChange={(range) => {
            setFilterData(prev => ({
              ...prev,
              from_date: range?.from ?? null,
              to_date: range?.to ?? null,
              date_range: range,
            }));
          }}
        />
      </FilterContainer>
      <div className="min-w-full mb-4 card">
        <div className="card-header !block">
          <div className='flex flex-wrap items-center justify-between'>
            <h3 className="card-title">Matters</h3>

            <div className='flex flex-wrap items-center gap-2'>
              <BulkDeleteActions
                canDelete={canDeleteMatter}
                selectedCount={selectedRows.length}
                bulkDeleteLoading={bulkDeleteLoading}
                allSelected={allSelected}
                onOpenConfirm={openBulkDelete}
                onToggleSelectPage={toggleSelectPage}
                confirmOpen={bulkDeleteOpen}
                onCloseConfirm={closeBulkDelete}
                onConfirmDelete={confirmBulkDelete}
                confirmMessage={`
                  You are about to delete ${selectedRows.length} Matter(s).
                  This will also delete all the associated invoices and documents with it.
                  You can restore it from the Recycle Bin within 30 days.
                `}
              />
              <button className='text-xs btn btn-sm btn-primary btn-outline' onClick={handleToggleFilter}>
                <KeenIcon icon="filter" className="w-fit btn-icon" />
                {showFilter ? 'Hide Advanced Filters' : 'Advanced Filters'}
              </button>
              {canCreateMatter && contactData?.status === 'active' && (
                <Link
                  // to={`/matters/create?contactId=${contactID}?tab=About`} 
                  to={`/matters/create?contactId=${contactID}`}
                  state={{ from: `/contacts/${contactID}?tab=Matters`, contactId: contactID }}
                  className='text-xs btn btn-sm btn-light'>
                  <KeenIcon icon="plus" className="" />
                  Add New
                </Link>
              )}
              {/* <Can I="handle" a="matter" ability={ability}>
              </Can> */}
            </div>
          </div>
        </div>

        <DataTable
          id="matter-table"
          className="table-with-checkbox"
          ajax={ajaxConfig}
          columns={columns}
          options={options}
          ref={tableRef}
        />

        <RequestFileUploadModal
          open={docUploadModal}
          title="Send Document Upload Request"
          initialValues={{ message: "", contact_id: "" }}
          validationSchema={validationSchema}
          onSubmit={({ message, contact_id }) => handleRequestFileUpload(message, contact_id)}
          onClose={closeFileUploadModal}
          loading={docUploadLoading}
          showPatientSelection={true}
          fields={[
            {
              name: "message",
              label: "Message",
              type: "textarea",
              rows: 6,
              optional: true,
              showCounter: true,
              max: 500,
              required: false
            },
          ]}
        />

        <ConfirmationModal
          show={isOpen}
          onClose={closeModal}
          onConfirm={handleDelete}
          btnText='Delete'
          message="Are you sure you want to delete this matter?"
          title="Are You Sure?"
        />
      </div>
    </>
  );
};

export { MattersTab }
